import { jsx, css } from '@emotion/react';
import LazyImage from "../../LazyImage";
import { connectLocation } from '../../location/LocationConnectors';
import {connectFavorites} from './Favorites/FavoritesConnectors';
import {FeatureImage} from './feature_utils';
import withDefaultPrevented from '../../../withDefaultPrevented';
import {ENABLED_FEATURE_FLAGS} from "./features";
import { PrefixWithLocale } from '../../Utilities';
import { altText } from './imageAlts';
import styled from '@emotion/styled';
import ShingleColorDescription from './ShingleColorDescription';

const pink = '#D40F7D'
const white = '#FFFFFF';
const heartOpen = `\f08a`;
const heartFilled = '\f004';

const shingleColor = css`
  display: inline-block;
  margin: 4px;
  text-decoration: none;
  width: 130px;
  cursor: pointer;
  position: relative;

  @media (min-width: 768px) {
    width: 150px;
    margin: 5px;
  }

  @media (min-width: 1200px) {
    width: 165px;
  }

  img {
    display: block;
    max-width: 100%;
    width: 100%;
    white-space: break-spaces;
    font-size: 14px;

    @media (min-width: 768px) {
      height: 150px;
    }

    @media (min-width: 1200px) {
      height: 165px;
    }
  }

  a {
    text-decoration: none;

    &:focus {
      outline: none;

      img {
        outline: 2px solid #d40f7d;
      }

      span {
        color: #d40f7d;
      }
    }
  }

  &.shingle-color--selected a img:not(.feature-flag) {
    outline: 5px solid #d40f7d;
    opacity: 1;
  }
`;

const colorName = css`
  display: block;
  font-size: 14px;
  font-weight: bold;
  line-height: 22px;
  margin-top: 4px;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const favoriteToggle = css`
  align-items: center;
  top: 112px;
  color: white;
  display: inline-flex;
  font-size: 20px;
  height: 52px;
  justify-content: center;
  position: absolute;
  right: 0;
  width: 52px;

  @media (max-width: 1200px) {
    top: 100px;
  }

  @media (max-width: 768px) {
    top: 80px;
  }
`;

const favoriteCheckbox = css`
  -moz-appearance: none;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-color: transparent;
  border: none;
  bottom: 0;
  cursor: pointer;
  height: 100%;
  margin: 0;
  outline: none;
  position: absolute;
  right: 0;
  width: 100%;

  & + i {
    cursor: pointer;
    font-size: 20px;
    position: relative;
  }

  & + i:before {
    content: "${heartOpen}";
  }

  & + i:hover:before {
    @media (min-width: 1200px) {
      color: ${white};
      content: "${heartFilled}";
    }
  }

  &:hover + i:before {
    @media (min-width: 1200px) {
      color: ${white};
      content: "${heartFilled}";
    }
  }

  &:checked + i:before {
    content: "${heartFilled}";
    color: ${pink};
  }

  &:checked + i:after {
    content: "${heartOpen}";
    font-weight: bold;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
  }

  &:focus + i {
    border: 1px solid white;
    padding: 3px;
  }
`;

const FavoriteIcon = styled.i`
  color: ${props => props.checked ? '#D40F7D' : '#FFFFFF'};
`

const FavoriteIconOverlay = styled.i`
  color: ${props => props.checked ? '#FFFFFF' : ''};
`
const Overlap = styled.div`
  i {
    position: absolute;
    cursor: pointer;
  }
`

const Color = ({shingle, color, favorites, removeFromFavorites, addToFavorites, houseView, onClick, isSelected, isPDP, t, address, locale}) => {
  const {uid, name, house_cropped_768x768_url2: houseImage, swatch_160x160_url: image, description} = color;
  const favorite = favorites.includes(color.uuid);
  const onToggleFavorite = () => favorite ? removeFromFavorites(color.uuid) : addToFavorites(color.uuid)
  const i18n_shingles = t('shingles.shingles').toLowerCase();

  return (
    <li css={shingleColor} className={`shingle-color ${isSelected && 'shingle-color--selected'}`}>
      <a
        href={PrefixWithLocale(`/roofing/shingles/${shingle.uid}?color=${uid}`, locale.code)}
        data-track="view-product"
        data-track-product-name={shingle.proper_name}
        data-track-product-color={name}
        tabIndex="0"
        onClick={onClick && withDefaultPrevented(() => onClick(color))}
      >
        <FeatureImage address={address} item={color} className='filters'
                      featuresSubset={ENABLED_FEATURE_FLAGS}/>
        <LazyImage
          src={image}
          alt={altText(color, 'swatch', locale.code, houseView ? '' : `${shingle.name} ${description || name} ${i18n_shingles}`)}
          display={houseView ? 'none' : 'block'}
        />
        <LazyImage
          src={houseImage}
          alt={altText(color, 'house_thumb', locale.code, houseView ? `${shingle.name} ${description || name} ${t('shingles.two_story_home')}` : '')}
          display={houseView ? 'block' : 'none'}
        />

        <span aria-hidden="true" css={colorName}>{name}</span>
        {isPDP && (
          <ShingleColorDescription
            description={description}
            className="tablet-desktop-only"
          />
        )}
      </a>

      <label
        css={favoriteToggle}
        className="favorite-toggle"
      >
        <input
          type="checkbox"
          css={favoriteCheckbox}
          checked={favorite}
          onChange={onToggleFavorite}
          data-track={favorite ? 'remove-favorite' : 'make-favorite'}
          data-track-product-name={shingle.uid}
          data-track-product-color={color.name}
        />
        <Overlap>
          <FavoriteIcon className={favorite ? "fa fa-heart" : "fa fa-heart-o"} checked={favorite} aria-hidden="true" />
          <FavoriteIconOverlay className={favorite ? "fa fa-heart-o" : ""} checked={favorite} aria-hidden="true" />
        </Overlap>
        <span
          className="sr-only">{favorite ? 'Remove' : 'Add'} {color.name} {isPDP ? '' : `${shingle.proper_name} ${i18n_shingles}`} {favorite ? 'from' : 'to'} ${t('shingles.favorites')}</span>
      </label>
    </li>
  );
};

export default connectLocation(connectFavorites(Color));
